/**
 * JobTypePricesSection — "Job type prices" on the customer form, READ-ONLY (gap 1, DEC-383, D-10).
 *
 * What this customer pays per job type today and where that price comes from: the customer's own
 * job-type price, the customer's general rate, or the job type's standard rate (D-03). The prices
 * are set in web-billing (Billing → Customer prices). Dispatch only reads them here: there is no
 * input, button or form for a price in this app. People who may set prices
 * (MANAGE_CUSTOMER_PRICES) get a link to the billing page; the server is the gate either way.
 */

import * as React from 'react';
import { formatCentsAsDollars } from '@lodex-pro/shared/money';
import { rateUnitSuffix } from '@lodex-pro/shared/rate-source';
import { customerPricesUrl } from '../../lib/billing-app-url';
import { labelStyle } from './form-bits';
import { subText } from './ui-bits';
import { useCustomerJobTypePricesView, type JobTypePriceViewRow } from './hooks';

const SOURCE_LABELS: Readonly<
  Record<NonNullable<JobTypePriceViewRow['today']>['source']['kind'], string>
> = {
  CUSTOMER_JOB_TYPE: 'this customer',
  CUSTOMER: 'customer rate',
  JOB_TYPE: 'standard',
};

const cell: React.CSSProperties = { padding: '4px 8px 4px 0', textAlign: 'left' };

export function JobTypePricesSection({
  haulerOperatorId,
  canEditInBilling,
}: {
  readonly haulerOperatorId: string;
  readonly canEditInBilling: boolean;
}): React.JSX.Element {
  const { data, isLoading, isError } = useCustomerJobTypePricesView(haulerOperatorId);
  const rows = data?.rows ?? [];

  return (
    <section
      aria-labelledby="jt-prices-title"
      data-testid="job-type-prices"
      style={{ borderTop: '1px solid var(--color-border-secondary)', paddingTop: '12px' }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <h3 id="jt-prices-title" style={{ ...labelStyle, marginBottom: '4px', flex: 1 }}>
          Job type prices
        </h3>
        {canEditInBilling && (
          <a
            href={customerPricesUrl(haulerOperatorId)}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: '12px', color: 'var(--color-accent-blue)' }}
          >
            Edit in Billing →
          </a>
        )}
      </div>
      <p style={{ ...subText, marginBottom: '8px' }}>
        Set in Billing → Customer prices. A job type&apos;s own price for this customer wins over
        the rates above.
      </p>
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && (
        <p style={{ ...subText, color: 'var(--color-danger)' }}>Could not load the prices.</p>
      )}
      {!isLoading && !isError && rows.length === 0 && <p style={subText}>No job types.</p>}
      {rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={cell}>Job type</th>
              <th style={cell}>Price today</th>
              <th style={cell}>Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.familyId}>
                <td style={cell}>
                  <div>{r.label}</div>
                  {r.otherUnit !== null && (
                    <div style={subText}>Price set in another unit; set a new one in Billing.</div>
                  )}
                </td>
                <td style={cell}>
                  {r.today === null
                    ? 'No price'
                    : `$${formatCentsAsDollars(r.today.rateCents)}${rateUnitSuffix(r.billingMethod)}`}
                </td>
                <td style={cell}>{r.today === null ? '—' : SOURCE_LABELS[r.today.source.kind]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
